import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { useRBAC } from "../contexts/RBACContext";
import Navbar from "../components/Navbar";
import CreateTicketModal from "../components/CreateTicketModal";
import { ITTicketTable } from "../components/tickets/ITTicketTable";
import { HRRequestTable } from "../components/tickets/HRRequestTable";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, ChevronLeft, ChevronRight, GripVertical, Ticket, UserPlus, UserMinus, RefreshCw, ArrowRightLeft, Calendar, Users, ArrowUpDown, ArrowUp, ArrowDown, MessageCircle, Bell, CheckSquare, X } from "lucide-react";
import { toast } from "sonner";

const HR_REQUEST_TYPES = [
  { value: "new", label: "New Hire", icon: UserPlus, color: "bg-green-500" },
  { value: "term", label: "Termination", icon: UserMinus, color: "bg-red-500" },
  { value: "change", label: "Change", icon: RefreshCw, color: "bg-blue-500" },
  { value: "transfer", label: "Transfer", icon: ArrowRightLeft, color: "bg-purple-500" },
  { value: "leave", label: "Leave", icon: Calendar, color: "bg-yellow-500" }
];

export default function TicketBoard({ user, logout, previewRole, setPreviewRole, previewDealership, setPreviewDealership, previewDepartment, setPreviewDepartment }) {
  const { hasPermission, isPrivilegedDepartment } = useRBAC();
  const [tickets, setTickets] = useState([]);
  const [totalTickets, setTotalTickets] = useState(0);
  const [assignees, setAssignees] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showHRModal, setShowHRModal] = useState(false);
  const [createType, setCreateType] = useState("it"); // "it" or "hr"
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [createdByFilter, setCreatedByFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const TICKETS_PER_PAGE = 50;
  const navigate = useNavigate();
  const location = useLocation();
  
  // HR Request form state
  const [dealerships, setDealerships] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [hrFormData, setHrFormData] = useState({
    employee_first_name: "",
    employee_last_name: "",
    employee_suffix: "",
    request_type: "",
    start_date: "",
    employee_id: "",
    dealership: user.dealership || "",
    department: user.department || "",
    job_title: "",
    notes: ""
  });
  const [submittingHR, setSubmittingHR] = useState(false);
  
  // HR Requests state
  const [hrRequests, setHrRequests] = useState([]);
  const [viewMode, setViewMode] = useState("it"); // "it", "hr", or "change"
  
  // Change Management state
  const [changeRequests, setChangeRequests] = useState([]);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [changeForm, setChangeForm] = useState({
    title: "", description: "", justification: "", change_type: "normal",
    priority: "medium", impact_assessment: "medium", risk_level: "medium",
    systems_affected: "", planned_start_date: "", planned_end_date: "",
    rollback_plan: "", dealerships: []
  });
  const [submittingChange, setSubmittingChange] = useState(false);
  const [dealershipsList, setDealershipsList] = useState([]);
  
  // Notifications state - track which tickets have unread notifications
  const [ticketsWithNotifications, setTicketsWithNotifications] = useState(new Set());
  
  // Determine effective role for permission checks (considering preview mode)
  const effectiveRole = previewRole || user.role;
  const effectiveDepartment = previewDepartment || user.department;
  
  // Use dynamic RBAC permissions for HR access
  const canCreateHR = hasPermission('hr_view', effectiveRole, effectiveDepartment);
  
  // Column configuration for reordering - IT Tickets
  const defaultColumns = [
    { id: "ticket", label: "Ticket", visible: true, width: "w-36" },
    { id: "created_at", label: "Created", visible: true, width: "w-28" },
    { id: "priority", label: "Priority", visible: true, width: "w-24" },
    { id: "summary", label: "Summary", visible: true, width: "flex-1" },
    { id: "created_by", label: "Created By", visible: true, width: "w-36" },
    { id: "assigned_to", label: "Assigned To", visible: true, width: "w-36" },
    { id: "status", label: "Status", visible: true, width: "w-24" },
  ];
  
  // Column configuration for reordering - HR Requests
  const defaultHRColumns = [
    { id: "request_number", label: "Ticket", visible: true, width: "w-36" },
    { id: "created_at", label: "Created", visible: true, width: "w-28" },
    { id: "type", label: "Type", visible: true, width: "w-28" },
    { id: "employee", label: "Employee", visible: true, width: "w-32" },
    { id: "dealership", label: "Dealership", visible: true, width: "w-36" },
    { id: "assigned_to", label: "Assigned To", visible: true, width: "w-32" },
    { id: "start_date", label: "Effective Date", visible: true, width: "w-28" },
    { id: "status", label: "Status", visible: true, width: "w-24" },
  ];
  
  const [columns, setColumns] = useState(defaultColumns);
  const [hrColumns, setHRColumns] = useState(defaultHRColumns);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [draggedHRColumn, setDraggedHRColumn] = useState(null);
  const [columnsLoaded, setColumnsLoaded] = useState(false);
  
  // Sorting state for IT Tickets
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // Sorting state for HR Requests
  const [hrSortColumn, setHRSortColumn] = useState(null);
  const [hrSortDirection, setHRSortDirection] = useState('asc');

  // Bulk action state
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [bulkAction, setBulkAction] = useState("");
  const [bulkAssignTo, setBulkAssignTo] = useState("");
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const canBulkAction = effectiveRole === "super_admin" || effectiveRole === "admin";

  // Load user's column preferences from the database
  useEffect(() => {
    const loadColumnPreferences = async () => {
      try {
        const response = await axios.get(`${API}/users/me/preferences`);
        if (response.data.ticket_columns && Array.isArray(response.data.ticket_columns)) {
          setColumns(response.data.ticket_columns);
        }
        if (response.data.hr_columns && Array.isArray(response.data.hr_columns)) {
          setHRColumns(response.data.hr_columns);
        }
      } catch (e) { console.error("Non-critical error:", e.message); } finally {
        setColumnsLoaded(true);
      }
    };
    loadColumnPreferences();
  }, [user.id]);

  // Save column preferences to the database
  const saveColumnPreferences = async (newColumns) => {
    try {
      await axios.patch(`${API}/users/me/preferences`, {
        ticket_columns: newColumns
      });
    } catch (e) { console.error("Non-critical error:", e.message); }
  };

  // Save HR column preferences to the database
  const saveHRColumnPreferences = async (newColumns) => {
    try {
      await axios.patch(`${API}/users/me/preferences`, {
        hr_columns: newColumns
      });
    } catch (e) { console.error("Non-critical error:", e.message); }
  };

  // Fetch tickets that have unread notifications for the current user
  const fetchTicketsWithNotifications = useCallback(async () => {
    if (effectiveRole === "end_user") {
      return;
    }
    try {
      const response = await axios.get(`${API}/notifications?unread_only=true&limit=100`);
      const ticketIds = new Set(
        response.data.notifications
          .filter(n => n.ticket_id)
          .map(n => n.ticket_id)
      );
      setTicketsWithNotifications(ticketIds);
    } catch (e) { console.error("Non-critical error:", e.message); }
  }, [effectiveRole]);

  useEffect(() => {
    fetchTickets();
    fetchTicketsWithNotifications();
    if (canCreateHR) {
      fetchHRRequests();
    }
  }, [effectiveRole, previewDealership, previewDepartment, location.pathname, fetchTicketsWithNotifications, currentPage, debouncedSearch, priorityFilter, createdByFilter, statusFilter]);

  // Clear bulk selection when filters, search, or page change
  useEffect(() => {
    setSelectedTickets(new Set());
  }, [currentPage, debouncedSearch, priorityFilter, createdByFilter, statusFilter]);

  // Fetch static reference data once
  useEffect(() => {
    fetchAssignees();
    fetchCreators();
    fetchChangeRequests();
    fetchDealershipsList();
    if (canCreateHR) {
      fetchDealerships();
      fetchDepartments();
    }
  }, []);

  // Auto-refresh tickets every 2 minutes
  useEffect(() => {
    const autoRefreshInterval = setInterval(() => {
      fetchTickets();
      fetchTicketsWithNotifications();
      if (canCreateHR) {
        fetchHRRequests();
      }
    }, 120 * 1000);

    return () => {
      clearInterval(autoRefreshInterval);
    };
  }, [effectiveRole, previewDealership, previewDepartment, fetchTicketsWithNotifications, currentPage, debouncedSearch, priorityFilter, createdByFilter, statusFilter]);

  // Refetch tickets when page becomes visible (user switches back to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchTickets();
        fetchTicketsWithNotifications();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchTicketsWithNotifications, currentPage, debouncedSearch, priorityFilter, createdByFilter, statusFilter]);

  // Debounce search input (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [priorityFilter, createdByFilter, statusFilter]);

  const fetchAssignees = async () => {
    try {
      const response = await axios.get(`${API}/tickets/assignees`);
      setAssignees(response.data);
    } catch (e) { console.error("Non-critical error:", e.message); }
  };

  const fetchCreators = async () => {
    try {
      const response = await axios.get(`${API}/tickets/creators`);
      setCreators(response.data);
    } catch (e) { console.error("Non-critical error:", e.message); }
  };

  // Handle URL parameters for filtering
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const priorityParam = searchParams.get('priority');
    const createdByParam = searchParams.get('created_by');
    const statusParam = searchParams.get('status');
    
    if (priorityParam) {
      setPriorityFilter(priorityParam);
      toast.info(`Showing ${priorityParam} priority tickets from Analytics`, {
        duration: 3000,
      });
    }
    if (createdByParam) {
      setCreatedByFilter(createdByParam);
      toast.info(`Showing tickets created by ${createdByParam}`, {
        duration: 3000,
      });
    }
    if (statusParam) {
      setStatusFilter(statusParam);
    }
  }, [location.search]);

  // Sort function for IT tickets (defined before useMemo that references it)
  const sortTickets = (ticketList, column, direction) => {
    return [...ticketList].sort((a, b) => {
      let aVal, bVal;
      
      switch (column) {
        case 'ticket':
          aVal = a.ticket_number || '';
          bVal = b.ticket_number || '';
          break;
        case 'created_at':
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        case 'priority': {
          const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
          aVal = priorityOrder[a.priority] || 0;
          bVal = priorityOrder[b.priority] || 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        case 'summary':
          aVal = a.title || '';
          bVal = b.title || '';
          break;
        case 'created_by':
          aVal = a.created_by_name || '';
          bVal = b.created_by_name || '';
          break;
        case 'assigned_to':
          aVal = a.assigned_to_name || 'zzz';
          bVal = b.assigned_to_name || 'zzz';
          break;
        case 'status': {
          const statusOrder = { new: 1, in_progress: 2, pending: 3, closed: 4 };
          aVal = statusOrder[a.status] || 0;
          bVal = statusOrder[b.status] || 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        default:
          return 0;
      }
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (direction === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  };

  const filteredTickets = useMemo(() => {
    if (!sortColumn) return tickets;
    return sortTickets(tickets, sortColumn, sortDirection);
  }, [tickets, sortColumn, sortDirection]);
  const fetchTickets = async () => {
    try {
      const headers = {};
      if (previewRole) {
        headers['X-Preview-Role'] = previewRole;
      }
      if (previewDealership) {
        headers['X-Preview-Dealership'] = previewDealership;
      }
      if (previewDepartment) {
        headers['X-Preview-Department'] = previewDepartment;
      }
      
      const params = new URLSearchParams();
      params.set("skip", String((currentPage - 1) * TICKETS_PER_PAGE));
      params.set("limit", String(TICKETS_PER_PAGE));
      
      // Status filtering
      if (statusFilter && statusFilter !== "all") {
        params.set("status", statusFilter);
      } else {
        params.set("exclude_status", "closed");
      }
      
      // Priority filtering
      if (priorityFilter && priorityFilter !== "all") {
        params.set("priority", priorityFilter);
      }
      
      // Created by filtering
      if (createdByFilter && createdByFilter !== "all") {
        params.set("created_by_name", createdByFilter);
      }
      
      // Text search
      if (debouncedSearch) {
        params.set("search", debouncedSearch);
      }
      
      const response = await axios.get(`${API}/tickets?${params.toString()}`, { headers });
      setTickets(response.data.tickets);
      setTotalTickets(response.data.total);
    } catch (error) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  const fetchDealerships = async () => {
    try {
      const response = await axios.get(`${API}/public/dealerships`);
      setDealerships(response.data);
    } catch (e) { console.error("Non-critical error:", e.message); }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/public/departments`);
      setDepartments(response.data);
    } catch (e) { console.error("Non-critical error:", e.message); }
  };

  const fetchHRRequests = async () => {
    try {
      const response = await axios.get(`${API}/hr-requests`);
      setHrRequests(response.data);
    } catch (e) { console.error("Non-critical error:", e.message); }
  };

  const fetchChangeRequests = async () => {
    try {
      const response = await axios.get(`${API}/change-requests`);
      setChangeRequests(response.data.filter(cr => cr.status !== "completed" && cr.status !== "rejected"));
    } catch (e) { console.error("Non-critical error:", e.message); }
  };

  const fetchDealershipsList = async () => {
    try {
      const response = await axios.get(`${API}/config/dealerships`);
      setDealershipsList(response.data || []);
    } catch (e) { console.error("Non-critical error:", e.message); }
  };

  const handleChangeSubmit = async (e) => {
    e.preventDefault();
    if (!changeForm.title.trim()) { toast.error("Please enter a title"); return; }
    if (!changeForm.description.trim()) { toast.error("Please enter a description"); return; }
    if (!changeForm.justification.trim()) { toast.error("Please enter justification"); return; }
    if (!changeForm.planned_start_date) { toast.error("Please select a start date"); return; }
    if (!changeForm.planned_end_date) { toast.error("Please select an end date"); return; }
    
    try {
      setSubmittingChange(true);
      await axios.post(`${API}/change-requests`, changeForm);
      toast.success("Change request submitted for approval");
      setShowChangeModal(false);
      setChangeForm({ title: "", description: "", justification: "", change_type: "normal", priority: "medium", impact_assessment: "medium", risk_level: "medium", systems_affected: "", planned_start_date: "", planned_end_date: "", rollback_plan: "", dealerships: [] });
      fetchChangeRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit change request");
    } finally {
      setSubmittingChange(false);
    }
  };

  const toggleDealershipSelection = (dealership) => {
    setChangeForm(prev => ({
      ...prev,
      dealerships: prev.dealerships.includes(dealership)
        ? prev.dealerships.filter(d => d !== dealership)
        : [...prev.dealerships, dealership]
    }));
  };



  const updateHRField = (field, value) => {
    setHrFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleHRSubmit = async (e) => {
    e.preventDefault();
    
    if (!hrFormData.employee_first_name.trim()) {
      toast.error("Please enter employee first name");
      return;
    }
    if (!hrFormData.employee_last_name.trim()) {
      toast.error("Please enter employee last name");
      return;
    }
    if (!hrFormData.request_type) {
      toast.error("Please select a request type");
      return;
    }
    if (!hrFormData.start_date) {
      toast.error("Please select an effective date");
      return;
    }
    if (!hrFormData.dealership) {
      toast.error("Please select a dealership");
      return;
    }
    if (!hrFormData.department) {
      toast.error("Please select a department");
      return;
    }
    if (!hrFormData.job_title.trim()) {
      toast.error("Please enter job title");
      return;
    }

    setSubmittingHR(true);
    try {
      await axios.post(`${API}/hr-requests`, hrFormData);
      toast.success("HR request submitted successfully");
      setShowHRModal(false);
      setHrFormData({
        employee_first_name: "",
        employee_last_name: "",
        employee_suffix: "",
        request_type: "",
        start_date: "",
        employee_id: "",
        dealership: user.dealership || "",
        department: user.department || "",
        job_title: "",
        notes: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit HR request");
    } finally {
      setSubmittingHR(false);
    }
  };

  // Sort HR requests
  const sortHRRequests = (requests, column, direction) => {
    return [...requests].sort((a, b) => {
      let aVal, bVal;
      
      switch (column) {
        case 'request_number':
          aVal = a.request_number || '';
          bVal = b.request_number || '';
          break;
        case 'created_at':
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        case 'type':
          aVal = a.request_type || '';
          bVal = b.request_type || '';
          break;
        case 'employee':
          aVal = `${a.employee_first_name || ''} ${a.employee_last_name || ''}`.trim();
          bVal = `${b.employee_first_name || ''} ${b.employee_last_name || ''}`.trim();
          break;
        case 'dealership':
          aVal = a.dealership || '';
          bVal = b.dealership || '';
          break;
        case 'assigned_to':
          aVal = a.assigned_to_name || 'zzz';
          bVal = b.assigned_to_name || 'zzz';
          break;
        case 'start_date':
          aVal = a.start_date ? new Date(a.start_date) : new Date(0);
          bVal = b.start_date ? new Date(b.start_date) : new Date(0);
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        case 'status':
          const statusOrder = { new: 1, in_progress: 2, pending: 3, closed: 4 };
          aVal = statusOrder[a.status] || 0;
          bVal = statusOrder[b.status] || 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        default:
          return 0;
      }
      
      // String comparison
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (direction === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  };

  // Handle column sort click for IT tickets
  const handleSort = (columnId) => {
    if (sortColumn === columnId) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, start with ascending
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };

  // Handle column sort click for HR requests
  const handleHRSort = (columnId) => {
    if (hrSortColumn === columnId) {
      setHRSortDirection(hrSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setHRSortColumn(columnId);
      setHRSortDirection('asc');
    }
  };

  // Get sorted HR requests - memoized
  const sortedHRRequests = useMemo(() => {
    if (!hrSortColumn) return hrRequests;
    return sortHRRequests(hrRequests, hrSortColumn, hrSortDirection);
  }, [hrRequests, hrSortColumn, hrSortDirection]);

  // Bulk action handlers
  const handleToggleSelect = (ticketId) => {
    setSelectedTickets(prev => {
      const next = new Set(prev);
      if (next.has(ticketId)) next.delete(ticketId);
      else next.add(ticketId);
      return next;
    });
  };

  const handleToggleSelectAll = (tickets) => {
    const allSelected = tickets.every(t => selectedTickets.has(t.id));
    if (allSelected) {
      setSelectedTickets(new Set());
    } else {
      setSelectedTickets(new Set(tickets.map(t => t.id)));
    }
  };

  const handleExitBulkMode = () => {
    setBulkMode(false);
    setSelectedTickets(new Set());
    setBulkAction("");
    setBulkAssignTo("");
  };

  const handleBulkSubmit = async () => {
    if (selectedTickets.size === 0) return;
    if (!bulkAction) { toast.error("Select an action"); return; }
    if (bulkAction === "reassign" && !bulkAssignTo) { toast.error("Select who to assign to"); return; }

    const confirmMsg = bulkAction === "close"
      ? `Close ${selectedTickets.size} ticket${selectedTickets.size > 1 ? "s" : ""}?`
      : `Reassign ${selectedTickets.size} ticket${selectedTickets.size > 1 ? "s" : ""}?`;
    if (!window.confirm(confirmMsg)) return;

    setBulkProcessing(true);
    try {
      const payload = {
        ticket_ids: Array.from(selectedTickets),
        action: bulkAction,
      };
      if (bulkAction === "reassign") payload.assigned_to = bulkAssignTo;

      const res = await axios.post(`${API}/tickets/bulk-update`, payload);
      toast.success(`${res.data.updated} ticket${res.data.updated !== 1 ? "s" : ""} updated`);
      handleExitBulkMode();
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkProcessing(false);
    }
  };

  // Filter out closed HR requests - they go to Ticket History
  const activeHRRequests = useMemo(() => {
    return sortedHRRequests.filter(request => request.status !== 'closed');
  }, [sortedHRRequests]);

  // Get active IT tickets count for the tab - use server total
  const getActiveTicketCount = () => {
    return totalTickets;
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      new: "bg-blue-100 text-blue-700 hover:bg-blue-200",
      in_progress: "bg-purple-100 text-purple-700 hover:bg-purple-200",
      pending: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
      closed: "bg-gray-100 text-gray-700 dark:text-white hover:bg-gray-200"
    };
    return classes[status] || classes.new;
  };

  const getPriorityBadgeClass = (priority) => {
    const classes = {
      low: "bg-green-100 text-green-700",
      medium: "bg-yellow-100 text-yellow-700",
      high: "bg-orange-100 text-orange-700",
      critical: "bg-red-100 text-red-700"
    };
    return classes[priority] || classes.medium;
  };

  // Pagination calculations - server-side driven
  const totalPages = Math.ceil(totalTickets / TICKETS_PER_PAGE);
  const startIndex = (currentPage - 1) * TICKETS_PER_PAGE;
  const endIndex = Math.min(startIndex + TICKETS_PER_PAGE, totalTickets);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
        <Navbar user={user} logout={logout} previewRole={previewRole} setPreviewRole={setPreviewRole} previewDealership={previewDealership} setPreviewDealership={setPreviewDealership} previewDepartment={previewDepartment} setPreviewDepartment={setPreviewDepartment} />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg text-slate-60 dark:text-white0">Loading tickets...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
        <Navbar user={user} logout={logout} effectiveRole={effectiveRole} previewRole={previewRole} setPreviewRole={setPreviewRole} previewDealership={previewDealership} setPreviewDealership={setPreviewDealership} previewDepartment={previewDepartment} setPreviewDepartment={setPreviewDepartment} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-80 dark:text-white0" data-testid="tickets-page-title">Tickets</h1>
            <p className="text-sm sm:text-base text-slate-600 dark:text-white mt-1">Manage and track all support tickets</p>
            {effectiveRole === "end_user" && previewRole && (
              <p className="text-xs text-orange-600 mt-1">Preview Mode: Showing only your tickets</p>
            )}
          </div>
          
          {/* Create Button with Toggle for HR */}
          <div className="flex flex-wrap items-center gap-2">
            {canCreateHR && (
              <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden mr-2">
                <button
                  onClick={() => setCreateType("it")}
                  className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors ${
                    createType === "it" 
                      ? "bg-blue-600 text-white" 
                      : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50"
                  }`}
                  data-testid="toggle-it-ticket"
                >
                  <Ticket className="w-4 h-4" />
                  IT Ticket
                </button>
                <button
                  onClick={() => setCreateType("hr")}
                  className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors ${
                    createType === "hr" 
                      ? "bg-[#7a1d42] text-white" 
                      : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50"
                  }`}
                  data-testid="toggle-hr-request"
                >
                  <Users className="w-4 h-4" />
                  HR Request
                </button>
              </div>
            )}
            <Button
              onClick={() => createType === "hr" ? setShowHRModal(true) : setShowCreateModal(true)}
              className={createType === "hr" ? "bg-[#7a1d42] hover:bg-[#5a1632]" : "bg-blue-600 hover:bg-blue-700"}
              data-testid="create-ticket-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              {createType === "hr" ? "New HR Request" : "Submit Ticket"}
            </Button>
          </div>
        </div>

        {/* Filters - Hidden for End Users */}
        {effectiveRole !== "end_user" && (
        <Card className="mb-6 border-0 shadow-md">
          <CardContent className="p-4">
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-white w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-tickets-input"
                />
              </div>
              
              {/* Filter Dropdowns */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-full" data-testid="priority-filter">
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
              <Select value={createdByFilter} onValueChange={setCreatedByFilter}>
                <SelectTrigger className="w-full" data-testid="created-by-filter">
                  <SelectValue placeholder="All Creators" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Creators</SelectItem>
                  {creators.map(creator => (
                    <SelectItem key={creator} value={creator}>
                      {creator}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full" data-testid="status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* View Mode Tabs - IT Tickets / HR Requests / Change Mgmt */}
        {canCreateHR && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-slate-500 dark:text-slate-400 mr-2">View:</span>
            <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <button
                onClick={() => setViewMode("it")}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                  viewMode === "it" 
                    ? "bg-blue-600 text-white" 
                    : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50"
                }`}
                data-testid="view-it-tickets"
              >
                <Ticket className="w-4 h-4" />
                IT Tickets ({getActiveTicketCount()})
              </button>
              <button
                onClick={() => setViewMode("hr")}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                  viewMode === "hr" 
                    ? "bg-[#7a1d42] text-white" 
                    : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50"
                }`}
                data-testid="view-hr-requests"
              >
                <Users className="w-4 h-4" />
                HR Requests ({activeHRRequests.length})
              </button>
              <button
                onClick={() => setViewMode("change")}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                  viewMode === "change" 
                    ? "bg-green-600 text-white" 
                    : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50"
                }`}
                data-testid="view-change-mgmt"
              >
                <RefreshCw className="w-4 h-4" />
                Change Mgmt ({changeRequests.length})
              </button>
            </div>
          </div>
        )}

        {/* Change Management Table View */}
        {viewMode === "change" && (
          <div className="bg-white dark:bg-[#2C2C2C] rounded-lg shadow-md overflow-hidden" data-testid="change-mgmt-table">
            <div className="px-4 py-3 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800 flex items-center justify-between">
              <p className="text-sm font-medium text-green-800 dark:text-green-300">Change Management Board</p>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => { fetchDealershipsList(); setShowChangeModal(true); }} data-testid="new-change-request-btn">
                <Plus className="w-4 h-4 mr-1" /> New Change Request
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-[#343434] border-b border-slate-200 dark:border-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase">Request #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase">Impact</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase">Assigned To</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-600">
                  {changeRequests.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-slate-500 dark:text-white">No active change requests</td>
                    </tr>
                  ) : (
                    changeRequests.map((cr) => {
                      const typeColors = { normal: "bg-blue-100 text-blue-700", emergency: "bg-red-100 text-red-700", standard: "bg-green-100 text-green-700" };
                      const priorityColors = { low: "bg-green-100 text-green-700", medium: "bg-yellow-100 text-yellow-700", high: "bg-orange-100 text-orange-700", critical: "bg-red-100 text-red-700" };
                      const statusColors = { pending: "bg-yellow-100 text-yellow-700", approved: "bg-blue-100 text-blue-700", in_progress: "bg-purple-100 text-purple-700", completed: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700" };
                      return (
                        <tr key={cr.id} className="hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors" onClick={() => navigate(`/change-requests/${cr.id}`)} data-testid={`change-row-${cr.id}`}>
                          <td className="px-4 py-4 whitespace-nowrap font-mono text-sm font-semibold text-slate-700 dark:text-white">{cr.request_number}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-white">{new Date(cr.created_at).toLocaleDateString()}</td>
                          <td className="px-4 py-4 whitespace-nowrap"><Badge className={typeColors[cr.change_type] || typeColors.normal}>{cr.change_type}</Badge></td>
                          <td className="px-4 py-4 text-sm font-medium text-slate-800 dark:text-white max-w-xs truncate">{cr.title}</td>
                          <td className="px-4 py-4 whitespace-nowrap"><Badge className={priorityColors[cr.priority] || priorityColors.medium}>{cr.priority}</Badge></td>
                          <td className="px-4 py-4 whitespace-nowrap"><Badge className={priorityColors[cr.impact_assessment] || priorityColors.medium}>{cr.impact_assessment}</Badge></td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-white">{cr.assigned_to_name || "Unassigned"}</td>
                          <td className="px-4 py-4 whitespace-nowrap"><Badge className={statusColors[cr.status] || statusColors.pending}>{cr.status.replace("_", " ")}</Badge></td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* HR Requests Table View */}
        {viewMode === "hr" && canCreateHR && (
          <HRRequestTable
            hrColumns={hrColumns}
            setHRColumns={setHRColumns}
            draggedHRColumn={draggedHRColumn}
            setDraggedHRColumn={setDraggedHRColumn}
            hrSortColumn={hrSortColumn}
            hrSortDirection={hrSortDirection}
            handleHRSort={handleHRSort}
            activeHRRequests={activeHRRequests}
            saveHRColumnPreferences={saveHRColumnPreferences}
            navigate={navigate}
          />
        )}

        {/* IT Tickets View */}
        {viewMode === "it" && (
          <>
            {/* Bulk Action Bar */}
            {canBulkAction && (
              <div className="mb-4">
                {!bulkMode ? (
                  <Button variant="outline" size="sm" onClick={() => setBulkMode(true)} className="gap-2" data-testid="enter-bulk-mode-btn">
                    <CheckSquare className="w-4 h-4" /> Bulk Actions
                  </Button>
                ) : (
                  <div className="flex flex-wrap items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800" data-testid="bulk-action-bar">
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300" data-testid="bulk-selected-count">
                      {selectedTickets.size} selected
                    </span>
                    <select
                      value={bulkAction}
                      onChange={(e) => setBulkAction(e.target.value)}
                      className="h-8 rounded-md border border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-800 text-sm px-2 text-slate-700 dark:text-white"
                      data-testid="bulk-action-select"
                    >
                      <option value="">Choose action...</option>
                      <option value="close">Close Tickets</option>
                      <option value="reassign">Reassign Tickets</option>
                    </select>
                    {bulkAction === "reassign" && (
                      <select
                        value={bulkAssignTo}
                        onChange={(e) => setBulkAssignTo(e.target.value)}
                        className="h-8 rounded-md border border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-800 text-sm px-2 text-slate-700 dark:text-white"
                        data-testid="bulk-assign-select"
                      >
                        <option value="">Assign to...</option>
                        {assignees.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    )}
                    <Button
                      size="sm"
                      disabled={selectedTickets.size === 0 || !bulkAction || bulkProcessing}
                      onClick={handleBulkSubmit}
                      data-testid="bulk-apply-btn"
                    >
                      {bulkProcessing ? "Processing..." : "Apply"}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleExitBulkMode} className="gap-1" data-testid="exit-bulk-mode-btn">
                      <X className="w-3 h-3" /> Cancel
                    </Button>
                  </div>
                )}
              </div>
            )}

            <ITTicketTable
              columns={columns}
              setColumns={setColumns}
              draggedColumn={draggedColumn}
              setDraggedColumn={setDraggedColumn}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              handleSort={handleSort}
              paginatedTickets={filteredTickets}
              ticketsWithNotifications={ticketsWithNotifications}
              setTicketsWithNotifications={setTicketsWithNotifications}
              saveColumnPreferences={saveColumnPreferences}
              navigate={navigate}
              selectedTickets={selectedTickets}
              onToggleSelect={handleToggleSelect}
              onToggleSelectAll={handleToggleSelectAll}
              bulkMode={bulkMode}
            />
            {totalTickets > TICKETS_PER_PAGE && (
              <div className="flex items-center justify-between mt-6 px-2" data-testid="pagination-controls">
                <div className="text-sm text-slate-600 dark:text-gray-400">
                  Showing {startIndex + 1}-{endIndex} of {totalTickets} tickets
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-testid="prev-page-btn">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;
                      return (
                        <Button key={pageNum} variant={currentPage === pageNum ? "default" : "outline"} size="sm" className="w-8 h-8 p-0" onClick={() => setCurrentPage(pageNum)} data-testid={`page-${pageNum}-btn`}>
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} data-testid="next-page-btn">
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
        </div>
      </div>

      {showCreateModal && (
        <CreateTicketModal
          user={user}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchTickets();
          }}
        />
      )}

      {/* HR Request Modal */}
      <Dialog open={showHRModal} onOpenChange={setShowHRModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#7a1d42]" />
              New HR Request
            </DialogTitle>
            <DialogDescription>
              Submit a new HR request for employee changes
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleHRSubmit} className="space-y-4">
            {/* Request Type Selection */}
            <div className="space-y-2">
              <Label>Request Type *</Label>
              <div className="grid grid-cols-5 gap-2">
                {HR_REQUEST_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => updateHRField("request_type", type.value)}
                      className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                        hrFormData.request_type === type.value
                          ? "border-[#7a1d42] bg-[#7a1d42]/10"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`hr-request-type-${type.value}`}
                    >
                      <div className={`w-8 h-8 rounded-full ${type.color} flex items-center justify-center`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs font-medium">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Employee Name */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hr_first_name">First Name *</Label>
                <Input
                  id="hr_first_name"
                  value={hrFormData.employee_first_name}
                  onChange={(e) => updateHRField("employee_first_name", e.target.value)}
                  placeholder="John"
                  data-testid="hr-employee-first-name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hr_last_name">Last Name *</Label>
                <Input
                  id="hr_last_name"
                  value={hrFormData.employee_last_name}
                  onChange={(e) => updateHRField("employee_last_name", e.target.value)}
                  placeholder="Doe"
                  data-testid="hr-employee-last-name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hr_suffix">Suffix</Label>
                <Select value={hrFormData.employee_suffix} onValueChange={(value) => updateHRField("employee_suffix", value)}>
                  <SelectTrigger data-testid="hr-employee-suffix">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">None</SelectItem>
                    <SelectItem value="Jr.">Jr.</SelectItem>
                    <SelectItem value="Sr.">Sr.</SelectItem>
                    <SelectItem value="II">II</SelectItem>
                    <SelectItem value="III">III</SelectItem>
                    <SelectItem value="IV">IV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Effective Date */}
            <div className="space-y-2">
              <Label htmlFor="hr_effective_date">Effective Date *</Label>
              <Input
                id="hr_effective_date"
                type="date"
                value={hrFormData.start_date}
                onChange={(e) => updateHRField("start_date", e.target.value)}
                data-testid="hr-effective-date"
                required
              />
            </div>

            {/* Employee ID */}
            <div className="space-y-2">
              <Label htmlFor="hr_employee_id">Employee ID</Label>
              <Input
                id="hr_employee_id"
                value={hrFormData.employee_id}
                onChange={(e) => updateHRField("employee_id", e.target.value)}
                placeholder="EMP-12345"
                data-testid="hr-employee-id"
              />
            </div>

            {/* Dealership */}
            <div className="space-y-2">
              <Label htmlFor="hr_dealership">Dealership *</Label>
              <Select value={hrFormData.dealership} onValueChange={(value) => updateHRField("dealership", value)} required>
                <SelectTrigger data-testid="hr-dealership">
                  <SelectValue placeholder="Select dealership" />
                </SelectTrigger>
                <SelectContent>
                  {dealerships.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Department */}
            <div className="space-y-2">
              <Label htmlFor="hr_department">Department *</Label>
              <Select value={hrFormData.department} onValueChange={(value) => updateHRField("department", value)} required>
                <SelectTrigger data-testid="hr-department">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Job Title */}
            <div className="space-y-2">
              <Label htmlFor="hr_job_title">Job Title *</Label>
              <Input
                id="hr_job_title"
                value={hrFormData.job_title}
                onChange={(e) => updateHRField("job_title", e.target.value)}
                placeholder="Sales Associate"
                data-testid="hr-job-title"
                required
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="hr_notes">Additional Notes</Label>
              <Textarea
                id="hr_notes"
                value={hrFormData.notes}
                onChange={(e) => updateHRField("notes", e.target.value)}
                placeholder="Any additional information..."
                rows={3}
                data-testid="hr-notes"
              />
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setShowHRModal(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submittingHR}
                className="bg-[#7a1d42] hover:bg-[#5a1632]"
                data-testid="submit-hr-request"
              >
                {submittingHR ? "Submitting..." : "Submit Request"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Change Request Modal */}
      <Dialog open={showChangeModal} onOpenChange={setShowChangeModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-green-700">New Change Management Request</DialogTitle>
            <DialogDescription>Submit a change request for approval</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangeSubmit} className="space-y-4">
            <div>
              <Label>Change Title *</Label>
              <Input value={changeForm.title} onChange={(e) => setChangeForm(p => ({...p, title: e.target.value}))} placeholder="Brief title for the change" data-testid="change-title-input" />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea value={changeForm.description} onChange={(e) => setChangeForm(p => ({...p, description: e.target.value}))} placeholder="Detailed description of the change" rows={3} data-testid="change-description-input" />
            </div>
            <div>
              <Label>Justification / Reason *</Label>
              <Textarea value={changeForm.justification} onChange={(e) => setChangeForm(p => ({...p, justification: e.target.value}))} placeholder="Why is this change needed?" rows={2} data-testid="change-justification-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Change Type</Label>
                <Select value={changeForm.change_type} onValueChange={(v) => setChangeForm(p => ({...p, change_type: v}))}>
                  <SelectTrigger data-testid="change-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={changeForm.priority} onValueChange={(v) => setChangeForm(p => ({...p, priority: v}))}>
                  <SelectTrigger data-testid="change-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Impact Assessment</Label>
                <Select value={changeForm.impact_assessment} onValueChange={(v) => setChangeForm(p => ({...p, impact_assessment: v}))}>
                  <SelectTrigger data-testid="change-impact-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Risk Level</Label>
                <Select value={changeForm.risk_level} onValueChange={(v) => setChangeForm(p => ({...p, risk_level: v}))}>
                  <SelectTrigger data-testid="change-risk-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Systems / Services Affected</Label>
              <Input value={changeForm.systems_affected} onChange={(e) => setChangeForm(p => ({...p, systems_affected: e.target.value}))} placeholder="e.g., Active Directory, VPN, Email Server" data-testid="change-systems-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Planned Start Date *</Label>
                <Input type="date" value={changeForm.planned_start_date} onChange={(e) => setChangeForm(p => ({...p, planned_start_date: e.target.value}))} data-testid="change-start-date" />
              </div>
              <div>
                <Label>Planned End Date *</Label>
                <Input type="date" value={changeForm.planned_end_date} onChange={(e) => setChangeForm(p => ({...p, planned_end_date: e.target.value}))} data-testid="change-end-date" />
              </div>
            </div>
            <div>
              <Label>Rollback Plan</Label>
              <Textarea value={changeForm.rollback_plan} onChange={(e) => setChangeForm(p => ({...p, rollback_plan: e.target.value}))} placeholder="Steps to revert the change if needed" rows={2} data-testid="change-rollback-input" />
            </div>
            <div>
              <Label>Dealerships (select multiple)</Label>
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 max-h-40 overflow-y-auto space-y-1" data-testid="change-dealerships-multi">
                {dealershipsList.length > 0 ? dealershipsList.map((d) => (
                  <label key={d.name || d} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 p-1 rounded text-sm">
                    <input
                      type="checkbox"
                      checked={changeForm.dealerships.includes(d.name || d)}
                      onChange={() => toggleDealershipSelection(d.name || d)}
                      className="rounded border-slate-300"
                    />
                    <span className="text-slate-700 dark:text-white">{d.name || d}</span>
                  </label>
                )) : (
                  <p className="text-sm text-slate-500">No dealerships configured</p>
                )}
              </div>
              {changeForm.dealerships.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {changeForm.dealerships.map((d) => (
                    <Badge key={d} variant="outline" className="text-xs">{d}</Badge>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowChangeModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={submittingChange} data-testid="submit-change-request-btn">
                {submittingChange ? "Submitting..." : "Submit for Approval"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
